import React, { useEffect, useState } from 'react';
import { Empty, Spin, Table, Row, Col, Typography } from 'antd';
import { LoadingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import apiClient from '../../../services/apiClient';

const { Text } = Typography;

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

export const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

export const currency = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(n);
};

/** Compact INR for dashboards — uses Cr / L for large amounts. */
export const currencyCompact = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(2)} L`;
  return currency(n);
};

export function CashFlowPanel({ isin, createdAt }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isin) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = { isin };
        if (createdAt) payload.investmentTimestamp = String(createdAt);
        const res = await apiClient.post(`${BASE_URL}/bond-cash-flow/calculate`, payload);
        if (!cancelled) setData(res.data?.data?.cashFlows || []);
      } catch (e) {
        if (!cancelled) setError('Failed to load cash flow data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isin, createdAt]);

  if (loading) {
    return (
      <div style={{ padding: '20px 0', textAlign: 'center' }}>
        <Spin indicator={<LoadingOutlined spin />} />
        <span style={{ marginLeft: 10, color: '#64748b', fontSize: 13 }}>Loading cash flows…</span>
      </div>
    );
  }
  if (error) return <div style={{ padding: '12px 16px', color: '#ef4444', fontSize: 13 }}>{error}</div>;
  if (!data || data.length === 0) {
    return <Empty description="No cash flow data available" style={{ padding: '20px 0' }} imageStyle={{ height: 48 }} />;
  }

  const cfColumns = [
    { title: 'Coupon Date', dataIndex: 'couponDate', key: 'couponDate', render: fmt, width: 130 },
    { title: 'Payment Date', dataIndex: 'paymentDate', key: 'paymentDate', render: fmt, width: 130 },
    { title: 'Cash Flow Type', dataIndex: 'cashFlows', key: 'cashFlows', width: 160 },
    { title: 'Days in Period', dataIndex: 'noOfDaysInCouponPeriod', key: 'noOfDaysInCouponPeriod', align: 'right', width: 120, render: fmt },
    { title: 'Day Count', dataIndex: 'dayCount', key: 'dayCount', width: 120, render: fmt },
    {
      title: 'Amount (₹)', dataIndex: 'amount', key: 'amount', align: 'right', width: 130,
      render: (v) => <span style={{ fontWeight: 600, color: '#0f766e' }}>{currency(v)}</span>,
    },
  ];

  return (
    <div style={{
      background: 'linear-gradient(135deg,#f0f9ff 0%,#e0f2fe 100%)',
      borderRadius: 10, padding: '14px 18px', margin: '4px 0', border: '1px solid #bae6fd',
    }}>
      <div style={{ marginBottom: 10, fontSize: 13, fontWeight: 600, color: '#0369a1' }}>
        📊 Cash Flow Schedule — <span style={{ fontFamily: 'monospace' }}>{isin}</span>
        <span style={{ marginLeft: 12, fontSize: 11, fontWeight: 400, color: '#64748b' }}>
          {data.length} payment{data.length !== 1 ? 's' : ''}
        </span>
      </div>
      <Table
        size="small"
        rowKey={(r, i) => `${r.couponDate}-${i}`}
        columns={cfColumns}
        dataSource={data}
        pagination={data.length > 12 ? { pageSize: 12, showSizeChanger: false } : false}
        scroll={{ x: 700 }}
        style={{ borderRadius: 8, overflow: 'hidden' }}
      />
    </div>
  );
}

export function CallPutPanel({ isin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isin) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`${BASE_URL}/master-isin/${isin}/options`);
        if (!cancelled) setData(res.data?.data);
      } catch (e) {
        console.error('Failed to load call/put options', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isin]);

  if (loading || !data) return null;

  const {
    putIndicator, callIndicator,
    putRedemptionDate, callRedemptionDate,
    putSpecifiedDates, callSpecifiedDates,
  } = data;

  return (
    <div style={{
      marginTop: 12,
      background: 'linear-gradient(135deg,#f0f9ff 0%,#e0f2fe 100%)',
      borderRadius: 10, padding: '14px 18px', border: '1px solid #bae6fd',
    }}>
      <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600, color: '#0369a1' }}>
        <InfoCircleOutlined style={{ marginRight: 8 }} />
        Call & Put Options — <span style={{ fontFamily: 'monospace' }}>{isin}</span>
      </div>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={4}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 2 }}>Call Option</Text>
          <Text strong style={{ fontSize: 13, color: '#075985' }}>{callIndicator ? 'Yes' : 'N/A'}</Text>
        </Col>
        <Col xs={24} md={4}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 2 }}>Call Dates</Text>
          <Text strong style={{ fontSize: 13, color: '#075985' }}>{callSpecifiedDates || 'N/A'}</Text>
        </Col>
        <Col xs={24} md={4}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 2 }}>Call Redemption</Text>
          <Text strong style={{ fontSize: 13, color: '#075985' }}>{callRedemptionDate || 'N/A'}</Text>
        </Col>
        <Col xs={24} md={4}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 2 }}>Put Option</Text>
          <Text strong style={{ fontSize: 13, color: '#075985' }}>{putIndicator ? 'Yes' : 'N/A'}</Text>
        </Col>
        <Col xs={24} md={4}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 2 }}>Put Dates</Text>
          <Text strong style={{ fontSize: 13, color: '#075985' }}>{putSpecifiedDates || 'N/A'}</Text>
        </Col>
        <Col xs={24} md={4}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 2 }}>Put Redemption</Text>
          <Text strong style={{ fontSize: 13, color: '#075985' }}>{putRedemptionDate || 'N/A'}</Text>
        </Col>
      </Row>
    </div>
  );
}
